import { useCallback, useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import Stage from '../ui/Stage';
import Transport from '../ui/Transport';
import { useMedia } from '../shell/AppShell';
import VideoPlayer from './VideoPlayer';

// The review frame's stage (DESIGN rev 2.1 §2.6 Review set): components/VideoPlayer, wrapped, not edited (its video.js
// config, sources, keyboard shortcuts and first-frame paint stay as they are), with the system Transport along the
// bottom: play, frame step, comment markers on the scrub bar, timecode / duration, speed, mute, full screen.
// VideoPlayer's own control bar is taken out of the DOM once the player is ready, so the Transport is the only bar.
// markers: [{ id, at, label }] (top-level comments); onMarker(marker) when one is clicked (the scrub bar also seeks).
// seekRef / onTimeUpdate / onPlayerReady pass straight through to VideoPlayer, as the pages used them before.
// playerRef (optional) receives the video.js player, for the page's type-to-pause.

const FRAME = 1 / 30;   // the step VideoPlayer's arrow keys use

export default function ReviewStage({ src, poster, label, comments = [], markers = [], activeMarker, onMarker, seekRef, onTimeUpdate, onPlayerReady, format, fps, playerRef }) {
  const wrap = useRef(null);
  const player = useRef(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [ratio, setRatio] = useState(null);
  const [full, setFull] = useState(false);
  // On a phone the bar keeps play, frame step, timecode and full screen; volume and speed are the device's.
  const phone = useMedia('(max-width: 600px)');
  // VideoPlayer keeps the callbacks it was first given (its effect runs per source), so they go through refs.
  const cb = useRef({ onTimeUpdate, onPlayerReady });
  cb.current = { onTimeUpdate, onPlayerReady };

  const handleTime = useCallback(t => { setTime(t); cb.current.onTimeUpdate?.(t); }, []);
  const handleReady = useCallback(() => {
    const el = wrap.current?.querySelector('.video-js');
    const p = el ? videojs.getPlayer(el) : null;
    if (p && !p.isDisposed()) {
      player.current = p;
      if (playerRef) playerRef.current = p;
      // Removed, not disposed: video.js still measures the bar for caption placement.
      const bar = p.getChild('ControlBar');
      if (bar) p.removeChild(bar);
      const sync = () => {
        setPlaying(!p.paused()); setMuted(p.muted()); setSpeed(p.playbackRate());
        const d = p.duration(); if (Number.isFinite(d) && d > 0) setDuration(d);
        const w = p.videoWidth(), h = p.videoHeight(); if (w && h) setRatio(`${w} / ${h}`);
      };
      p.on(['play', 'pause', 'ended', 'volumechange', 'ratechange', 'durationchange', 'loadedmetadata'], sync);
      sync();
    }
    cb.current.onPlayerReady?.();
    // playerRef is a ref object; reading it here is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const on = () => setFull(document.fullscreenElement === wrap.current);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);

  const live = () => (player.current && !player.current.isDisposed() ? player.current : null);
  const seek = t => { const p = live(); if (!p) return; p.currentTime(t); handleTime(t); };
  const playPause = () => { const p = live(); if (!p) return; if (p.paused()) p.play()?.catch?.(() => {}); else p.pause(); };
  const step = dir => { const p = live(); if (!p) return; seek(Math.min(p.duration() || 0, Math.max(0, p.currentTime() + dir * FRAME))); };
  const fullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else wrap.current?.requestFullscreen?.().catch(() => {});
  };

  return (
    <div className="ui-review-stage" ref={wrap} style={ratio ? { '--media-ratio': ratio } : undefined}>
      <Stage label={label} transport={(
        <Transport time={time} duration={duration} playing={playing} onPlayPause={playPause} onSeek={seek} onFrameStep={step}
          markers={markers} activeMarker={activeMarker} onMarker={onMarker} format={format} fps={fps}
          speed={speed} onSpeed={phone ? undefined : s => live()?.playbackRate(s)}
          muted={muted} onMute={phone ? undefined : () => { const p = live(); if (p) p.muted(!p.muted()); }}
          fullscreen={full} onFullscreen={fullscreen} />
      )}>
        <div className="ui-review-media">
          <VideoPlayer src={src} poster={poster} comments={comments} seekRef={seekRef} onTimeUpdate={handleTime} onPlayerReady={handleReady} />
        </div>
      </Stage>
    </div>
  );
}
